/* eslint-disable @typescript-eslint/no-non-null-assertion */
import './Quote.css';

import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { AuthProvider, useAuth } from '../../auth/AuthProvider';
import { RatingQuotingClient } from '../../backend-client/ratingQuotingBackend';
import Loading from '../../components/Loading/Loading';
import { BusinessInfo, useGetBusinessInfoQuery } from '../../generated/graphql';
import {
  GetQuoteExpired,
  GetQuoteOk,
  QuoteResponse
} from '../../generated/rating-quoting-graphql';
import getEnv from '../../utils/getEnv';
import { Context, ContextValue } from '../../utils/store';
import { ReturnToQuotePopup } from './components/ReturnToQuotePopup/ReturnToQuotePopup';
import Quote from './Quote';
import {
  handleQuotePageRedirect,
  shouldNavigateToClientAgreement,
  useQuotePageRedirectQuery
} from './useQuotePageRedirect';

interface QuoteParams {
  applicationId: string;
}

interface Option {
  text: string;
  value: string;
}

export interface CoveragesDataInput {
  name: string;
  chooseCoverage: boolean;
  finalPremium: number;
  limits: Record<string, number>;
  limitsOptions?: Record<string, Array<Option>>;
}

const { REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL } = getEnv();

/**
 * Props for inner component
 * Contains all pre-fetched data and validated props
 */
interface QuotePageContentProps {
  applicationId: string;
  quoteId: string;
  versionData: WorkflowVersion;
  quoteDataFromRatingQuoting: QuoteResponse;
  businessInfoData: BusinessInfo;
}

/**
 * QuotePageContent - Inner component rendered inside AuthProvider
 *
 * Responsibilities:
 * - Only renders when AuthProvider context is available
 * - Handles presentation logic (quote display, redirects, popups)
 * - Safely uses useAuth() hook for authentication
 * - Handles quote response types (OK, Expired)
 *
 * Note: All data is pre-validated by parent, so no need to re-fetch
 */
const QuotePageContent: React.FC<QuotePageContentProps> = ({
  applicationId,
  quoteId,
  versionData,
  quoteDataFromRatingQuoting,
  businessInfoData
}) => {
  const context: ContextValue | null = useContext(Context);

  const { isAuthenticated } = useAuth();

  const [isTnCLinkSent, setIsTnCLinkSent] = useState<boolean>(false);
  const [
    isRedirectCheckComplete,
    setIsRedirectCheckComplete
  ] = useState<boolean>(false);

  const isCustomerAgreementActive = context
    ? context.isCustomerAgreementActive
    : false;
  const setCustomerAgreementActive = context
    ? context.setCustomerAgreementActive
    : undefined;

  const {
    loading: isGetQuotePageRedirectLoading,
    error: getQuotePageRedirectError,
    data: getQuotePageRedirectData
  } = useQuotePageRedirectQuery(applicationId);

  // Determine quote type and extract data
  // quoteDataFromRatingQuoting can be: GetQuoteOk, GetQuoteExpired, or GetQuoteDecline
  let returnToQuoteExpired: GetQuoteExpired | undefined = undefined;
  let quoteData: GetQuoteOk | undefined = undefined;

  if (quoteDataFromRatingQuoting?.__typename === 'GetQuoteOk') {
    quoteData = quoteDataFromRatingQuoting;
  } else if (quoteDataFromRatingQuoting?.__typename === 'GetQuoteExpired') {
    returnToQuoteExpired = quoteDataFromRatingQuoting;
  }

  // Handle page redirects based on quote state
  // This effect checks if the user should be redirected away from this page
  // (e.g., to login or agreement page). It runs asynchronously via handleQuotePageRedirect.
  // We only mark isRedirectCheckComplete after the check finishes to ensure any
  // redirects happen before we render other UI (like ReturnToQuotePopup).
  useEffect(() => {
    if (getQuotePageRedirectData && !isGetQuotePageRedirectLoading) {
      // Extract isTnCLinkSent from redirect data
      if (getQuotePageRedirectData.getQuotePageRedirect?.ownerData) {
        setIsTnCLinkSent(
          getQuotePageRedirectData.getQuotePageRedirect.ownerData
            .isTnCLinkSent ?? false
        );
      }

      // Check if user should be redirected and handle accordingly
      handleQuotePageRedirect(
        getQuotePageRedirectData,
        quoteId,
        isAuthenticated,
        returnToQuoteExpired,
        applicationId
      ).then((shouldHaveRedirected) => {
        // CRITICAL: Only allow rendering if NO redirect is happening
        // If shouldHaveRedirected === true, never set isRedirectCheckComplete to true
        // This keeps the component in Loading state while redirect occurs
        if (!shouldHaveRedirected) {
          // Check if should navigate to Client & Agreement page
          const shouldNavigate = shouldNavigateToClientAgreement(
            getQuotePageRedirectData,
            isAuthenticated,
            returnToQuoteExpired
          );
          if (shouldNavigate && setCustomerAgreementActive) {
            setCustomerAgreementActive(true);
          }
          // Only mark as complete if NO redirect will happen
          setIsRedirectCheckComplete(true);
        }
        // If shouldHaveRedirected === true, we don't set the flag
        // The redirect will handle sending user away from this page
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getQuotePageRedirectData,
    quoteId,
    isAuthenticated,
    returnToQuoteExpired,
    applicationId,
    isGetQuotePageRedirectLoading
  ]);

  if (getQuotePageRedirectError) {
    return (
      <div className="m-auto mt-20 error-bubble text-mobileFont md:text-base">
        Sorry! It is not you, it is us. We will get in touch with you to
        retrieve your quote.
      </div>
    );
  }

  if (isGetQuotePageRedirectLoading) {
    return <Loading />;
  }

  // Handle expired quote case
  // Only show the popup after redirect check is complete to ensure user isn't redirected away
  if (quoteDataFromRatingQuoting?.__typename === 'GetQuoteExpired') {
    // Guard: don't render popup until we've finished checking for redirects
    if (!isRedirectCheckComplete) {
      return <Loading />;
    }

    const closePopup = () => {
      window.location.reload();
    };

    if (
      (quoteDataFromRatingQuoting.isQuoteExpired ||
        quoteDataFromRatingQuoting.isGracePeriodExpired) &&
      quoteDataFromRatingQuoting.quoteExpireDate
    ) {
      return (
        <ReturnToQuotePopup
          isQuoteExpired={true}
          applicationId={applicationId}
          quoteExpireDate={quoteDataFromRatingQuoting.quoteExpireDate}
          closeAction={closePopup}
        />
      );
    } else if (
      quoteDataFromRatingQuoting.isEffectiveDateExpired &&
      quoteDataFromRatingQuoting.quoteExpireDate
    ) {
      return (
        <ReturnToQuotePopup
          isQuoteExpired={false}
          applicationId={applicationId}
          quoteExpireDate={quoteDataFromRatingQuoting.quoteExpireDate!}
          closeAction={closePopup}
        />
      );
    }
  }

  // At this point, quoteData must be defined (all other paths have returned)
  if (!quoteData) {
    return <Loading />;
  }

  // Render active quote
  return (
    <Quote
      applicationId={applicationId}
      versionData={versionData}
      quoteData={quoteData} // This quote is getQuoteOk type, guaranteed by the checks above
      shouldShowCustomerAgreement={isCustomerAgreementActive}
      isTnCLinkSent={isTnCLinkSent}
      businessInfo={businessInfoData}
    />
  );
};

/**
 * QuotePage - Container component that manages all quote page data fetching
 *
 * Flow:
 * 1. Fetch version data from VersionControllerClient
 * 2. Use version data to fetch quote from RatingQuotingClient
 * 3. Extract quoteId from quote response
 * 4. Fetch business info in parallel (via useGetBusinessInfoQuery)
 * 5. Once all data ready, conditionally render AuthProvider with quoteId
 * 6. AuthProvider validates auth context with the quoteId
 * 7. Only then render QuotePageContent (inner component)
 *
 * Key points:
 * - AuthProvider only mounts when quoteId is available
 * - This prevents auth context from being created with undefined quoteId
 * - No timing race conditions for isAuthenticated value
 * - All errors handled before rendering children
 * - Progress set to 100% immediately on page load
 */
export const QuotePage: React.FC = () => {
  const { applicationId } = useParams<QuoteParams>();
  const context: ContextValue | null = useContext(Context);

  // Step 1: Initialize state for fetched data
  const [versionData, setVersionData] = useState<WorkflowVersion>();
  const [versionDataError, setVersionDataError] = useState();

  const [
    quoteDataFromRatingQuoting,
    setQuoteDataFromRatingQuoting
  ] = useState<QuoteResponse>();
  const [
    quoteDataFromRatingQuotingError,
    setQuoteDataFromRatingQuotingError
  ] = useState();

  const [quoteId, setQuoteId] = useState<string | undefined>(undefined);

  // Fetch business info in parallel
  const {
    loading: isBusinessInfoLoading,
    error: businessInfoError,
    data: businessInfoData
  } = useGetBusinessInfoQuery({
    variables: { applicationId }
  });

  // Step 3: Extract quoteId from quote response when available
  // The quoteId is needed to initialize AuthProvider. We only extract when we have
  // a GetQuoteOk or GetQuoteExpired response (both contain quoteId).
  // Once extracted, it triggers conditional AuthProvider rendering below.
  if (
    (quoteDataFromRatingQuoting?.__typename === 'GetQuoteOk' ||
      quoteDataFromRatingQuoting?.__typename === 'GetQuoteExpired') &&
    !quoteId
  ) {
    setQuoteId(quoteDataFromRatingQuoting.quoteId);
  }

  // Step 1: Fetch version info from Version Controller
  // This is the root fetch that unlocks everything else - the version includes
  // the backend URL needed by the RatingQuoting client. Runs once on mount.
  useEffect(() => {
    const versionControllerClient = new VersionControllerClient(
      REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
    );
    versionControllerClient
      .getVersionFromApplication(applicationId)
      .then((data) => {
        setVersionData(data);
      })
      .catch((err) => {
        setVersionDataError(err);
      });
  }, [applicationId]);

  // Step 2: Fetch quote data from Rating & Quoting backend
  // This runs only after versionData arrives and contains the backend URL.
  // We fetch in parallel with businessInfo (Step 1 of businessInfo effect).
  useEffect(() => {
    if (versionData) {
      const ratingQuotingClient = new RatingQuotingClient(
        versionData.quoteBackendVersion
      );
      ratingQuotingClient
        .getQuote(applicationId)
        .then((data) => {
          setQuoteDataFromRatingQuoting(data);
        })
        .catch((err) => setQuoteDataFromRatingQuotingError(err));
    }
  }, [applicationId, versionData]);

  // Step 4: Set progress to 100% immediately on page load
  // We don't wait for data fetches to complete - the progress bar shows completion
  // as soon as the user navigates to this page. Context dependency ensures this
  // runs once per context instance (once on mount).
  useEffect(() => {
    if (context) {
      context.setProgress(100);
    }
  }, [context]);

  // Step 5: Error guard - check if any of the three parallel fetches failed
  // If version, quote, or business info fetch failed, show error to user.
  // This prevents invalid/undefined data from being passed to child components.
  if (
    versionDataError ||
    quoteDataFromRatingQuotingError ||
    businessInfoError
  ) {
    return (
      <div className="m-auto mt-20 error-bubble text-mobileFont md:text-base">
        Sorry! It is not you, it is us. We will get in touch with you to
        retrieve your quote.
      </div>
    );
  }

  // Step 6: Loading guard - wait for all fetch operations to complete
  // We need all three data sources before proceeding:
  // - versionData: contains backend URL for quote fetching
  // - quoteDataFromRatingQuoting: the actual quote (needed by step 3)
  // - businessInfoData: company info for display
  // Business info loading includes businessInfoData becoming available.
  if (
    !versionData ||
    !quoteDataFromRatingQuoting ||
    isBusinessInfoLoading ||
    !businessInfoData
  ) {
    return <Loading />;
  }

  // Step 7a: Handle declined quote - redirect to completion page
  // If the rating/quoting backend declined the application (rate, don't quote),
  // send user to completion page instead of showing quote interface.
  if (quoteDataFromRatingQuoting.__typename === 'GetQuoteDecline') {
    return <Redirect to="/complete" />;
  }

  // Step 7b: Guard before AuthProvider initialization
  // Don't render AuthProvider until quoteId is available (extracted in step 3).
  // This prevents "useAuth called without provider" errors - the quoteId must exist
  // before AuthProvider is mounted, ensuring no timing race conditions.
  if (!quoteId) {
    return <Loading />;
  }

  // Step 8: Render the page with authenticated context
  // All data fetches are complete and quoteId is available. AuthProvider wraps
  // QuotePageContent to make useAuth() safe to call in child and nested components.
  // Pass only the extracted data to child - businessInfoData.getBusinessInfo extracts
  // the typed object from the GraphQL query response.
  return (
    <AuthProvider quoteId={quoteId}>
      <QuotePageContent
        applicationId={applicationId}
        quoteId={quoteId}
        versionData={versionData}
        quoteDataFromRatingQuoting={quoteDataFromRatingQuoting}
        businessInfoData={businessInfoData.getBusinessInfo}
      />
    </AuthProvider>
  );
};

export default QuotePage;
